import React from 'react';
import { Card, CardContent } from './ui/card';
import { Target, Eye, Users, Award, Globe, Package, BarChart3 } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [whoWeAreRef, whoWeAreVisible] = useScrollAnimation();
  const [missionRef, missionVisible] = useScrollAnimation();
  const [visionRef, visionVisible] = useScrollAnimation();
  const [valuesRef, valuesVisible] = useScrollAnimation();
  const [whyRef, whyVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-white to-blue-50/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 animate-on-scroll ${headerVisible ? 'animate-visible' : ''}`}
        >
          <h2 className="headline-font text-4xl md:text-5xl text-slate-900 mb-4">{t('about.title')}</h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-6"></div>
        </div>

        {/* Who We Are */}
        <div 
          ref={whoWeAreRef}
          className={`max-w-5xl mx-auto mb-20 animate-on-scroll ${whoWeAreVisible ? 'animate-visible' : ''}`}
        >
          <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="subheadline-font text-2xl text-slate-900 mt-2">{t('about.whoWeAre')}</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t('about.whoWeAreText')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Why AIM TRADE? - 3 Boxes */}
        <div
          ref={whyRef}
          className={`max-w-6xl mx-auto mb-20 animate-on-scroll ${whyVisible ? 'animate-visible' : ''}`}
        >
          <h3 className="subheadline-font text-3xl text-slate-900 text-center mb-12">{t('about.whyTitle')}</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Regional & Global Footprint */}
            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
              <CardContent className="p-8 text-center">
                <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold text-xl mb-4">{t('about.why1Title')}</h4>
                <p className="text-gray-300 leading-relaxed">
                  {t('about.why1Text')}
                </p>
              </CardContent>
            </Card>

            {/* End-to-End Supply Chain */}
            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
              <CardContent className="p-8 text-center">
                <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Package className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="font-bold text-xl mb-4">{t('about.why2Title')}</h4>
                <p className="text-blue-100 leading-relaxed">
                  {t('about.why2Text')}
                </p>
              </CardContent>
            </Card>

            {/* Market Intelligence */}
            <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
              <CardContent className="p-8 text-center">
                <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BarChart3 className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold text-xl mb-4">{t('about.why3Title')}</h4>
                <p className="text-gray-300 leading-relaxed">
                  {t('about.why3Text')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-20">
          {/* Mission */}
          <div
            ref={missionRef}
            className={`animate-on-scroll-left ${missionVisible ? 'animate-visible' : ''}`}
          >
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
              <CardContent className="p-8">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="subheadline-font text-xl text-slate-900 mb-4">{t('about.mission')}</h3>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.missionText')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Vision */}
          <div
            ref={visionRef}
            className={`animate-on-scroll-right ${visionVisible ? 'animate-visible' : ''}`}
          >
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
              <CardContent className="p-8">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="subheadline-font text-xl text-slate-900 mb-4">{t('about.vision')}</h3>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.visionText')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Core Values */}
        <div 
          ref={valuesRef}
          className={`bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-5xl mx-auto animate-on-scroll-scale ${valuesVisible ? 'animate-visible' : ''}`}
        >
          <div className="flex items-center gap-3 mb-6 justify-center">
            <Award className="w-8 h-8 text-blue-600" />
            <h3 className="subheadline-font text-2xl text-slate-900">{t('about.coreValues')}</h3>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            {t('about.coreValueText')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;